import { useNavigate } from 'react-router-dom';
import { Briefcase, Users, UserPlus, Zap, Check, X, Crown, User, ExternalLink, Infinity as InfinityIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useBillingStatus } from '@/hooks/useBillingStatus';
import { CapUsageWidgets } from '@/components/dashboard/CapUsageWidgets';
import { ScheduledPlanChangeBanner } from '@/components/billing/ScheduledPlanChangeBanner';
import { AGENCY_SELF_SERVE_ENABLED } from '@/lib/productFlags';

const PLAN_LABEL: Record<string, string> = {
  freemium_trial: 'Trial',
  sme_starter: 'Starter',
  sme_growth: 'Growth',
  sme_professional: 'Professional',
  agency_starter: 'Agency Starter',
  agency_medium: 'Agency Medium',
  agency_professional: 'Agency Professional',
  agency_enterprise: 'Agency Enterprise',
  unlimited: 'Unlimited',
  starter: 'Starter',
  growth: 'Growth',
  professional: 'Professional',
};

const PLAN_BADGE_TONE: Record<string, string> = {
  freemium_trial: 'bg-warning/10 text-warning border-warning/30',
  sme_starter: 'bg-info/10 text-info border-info/30',
  sme_growth: 'bg-success/10 text-success border-success/30',
  sme_professional: 'bg-primary/10 text-primary border-primary/30',
  agency_starter: 'bg-brand-primary/10 text-brand-primary border-brand-primary/30',
  agency_medium: 'bg-brand-primary/10 text-brand-primary border-brand-primary/30',
  agency_professional: 'bg-brand-primary/15 text-brand-primary border-brand-primary/40',
  agency_enterprise: 'bg-brand-accent/15 text-brand-accent border-brand-accent/40',
  unlimited: 'bg-brand-accent/15 text-brand-accent border-brand-accent/40',
};

/**
 * Helper: format a per-company cap. `aiUser` and `aiCompany` totals on the
 * BillingStatus payload are already per-company (agency tiers are configured
 * per-company in v3.2 — see `getEffectiveCompanyLimits`).
 *
 * `>= 999_999` → unlimited icon, `0` → ❌, else numeric.
 */
function CapCell({ value }: { value: number }) {
  if (value <= 0) return <X className="h-4 w-4 text-muted-foreground/40" />;
  if (value >= 999_999) return <InfinityIcon className="h-4 w-4 text-primary" />;
  return <span className="font-medium tabular-nums">{value.toLocaleString()}</span>;
}

/**
 * v3.2 polish (22/05/26): read-only Settings → Piano tab.
 *
 * Audience: every member of a company (admin/write/read) — gives non-owners
 * visibility on WHICH plan is applied to the company they work in, including
 * the per-company caps inherited from the owner's user-level subscription.
 *
 * Cannot change anything from here — subscription management lives on
 * `/profile?tab=billing` (owner only). Both members and the owner see the
 * "Gestisci abbonamento" CTA, but for non-owners clicking it lands them on
 * their own (lower-tier) plan, which is the correct expectation in v3.2
 * user-level billing.
 */
export function CompanyPlanTab() {
  const { t, language } = useLanguage();
  const { user, selectedCompany } = useAuth();
  const navigate = useNavigate();
  const { data: status, isLoading, error, refetch } = useBillingStatus();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error || !status) {
    return (
      <Card>
        <CardContent className="py-8 flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">{t('settings.plan.loadError')}</p>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            {t('usage.retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const planId = status.planId;
  const planLabel = PLAN_LABEL[planId] ?? planId;
  const planTone = PLAN_BADGE_TONE[planId] ?? 'bg-muted text-foreground';
  const isAgencyPlan = planId.startsWith('agency_');
  const isTrial = planId === 'freemium_trial';

  // Owner attribution: BE adds a best-effort `owner` block on
  // GET /companies/:id/billing/status (v3.2 polish 22/05/26). Hidden when
  // missing (legacy data / lookup failure).
  // v3.4 (25/05/26): also gate on `user.canManagePlan` — an `invited_member`
  // must never see plan-management CTAs even if some upstream cache misread
  // them as owner. Both flags must align for the CTA to render.
  const owner = status.owner;
  const canManagePlan = user?.canManagePlan ?? true;
  // BUG FIX (25/05/26): User type uses `id` (mapped from firebase uid in
  // AuthContext), not `uid`. The previous `user.uid === owner.uid` compared
  // undefined → owner.uid → always false → owners never saw the "Gestisci
  // abbonamento" CTA.
  const isOwner = !!owner && !!user && owner.uid === user.id && canManagePlan;
  const ownerLabel = owner?.name || owner?.email || (owner ? '—' : '');


  const priceFormatted = (() => {
    if (planId === 'agency_enterprise' || status.isCustom) return t('settings.plan.priceCustom');
    if (isTrial) return t('settings.plan.priceFree');
    const n = status.priceMonthly ?? 0;
    return new Intl.NumberFormat(language === 'it' ? 'it-IT' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(n) + '/' + t('settings.plan.perMonth');
  })();

  const limits = status.limits;

  // Sprint 2 follow-up (22/05/26 ~20:05) — inheritance clarity.
  // The company tab is READ-ONLY by design: the subscription SSOT is on
  // the owner's profile (`/profile?tab=billing`). When the owner schedules
  // a tier change (downgrade/upgrade defer), every member of every owned
  // company should see the pending change as INFORMATION — not as an
  // action they can take. The same banners live on the Profile tab (Agency
  // view) where the owner actually clicks Upgrade/Downgrade.
  const scheduled = status.scheduled;
  const cancelAtPeriodEnd = !scheduled ? status.cancelAtPeriodEnd : undefined;

  return (
    <div className="space-y-6">
      <ScheduledPlanChangeBanner
        scheduled={scheduled}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        currentPlanLabel={planLabel}
        owner={owner}
        language={language}
      />

      {/* Hero card — plan badge + price + owner attribution */}
      <Card className="border-brand-primary/30 bg-gradient-to-br from-brand-primary/5 via-background to-background">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-2 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {isAgencyPlan ? (
                  <Crown className="h-5 w-5 text-brand-primary shrink-0" />
                ) : (
                  <Briefcase className="h-5 w-5 text-brand-primary shrink-0" />
                )}
                <Badge variant="outline" className={`text-sm font-semibold px-2.5 py-0.5 ${planTone}`}>
                  {planLabel}
                </Badge>
                <span className="text-sm font-medium text-muted-foreground">
                  {priceFormatted}
                </span>
              </div>
              <CardTitle className="text-base font-semibold">
                {t('settings.plan.hero.title')}
              </CardTitle>
              {owner && ownerLabel && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <User className="h-3 w-3 shrink-0" />
                  {isOwner
                    ? t('settings.plan.managedByYou')
                    : t('settings.plan.managedBy', { name: ownerLabel })}
                </p>
              )}
            </div>
            {/* CTA — only owner can manage the subscription */}
            {isOwner && (
              <Button
                size="sm"
                variant="default"
                className="gap-1.5 shrink-0"
                onClick={() => navigate('/profile?tab=billing')}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                {t('settings.plan.cta.manage')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            {isAgencyPlan
              ? t('settings.plan.hero.descAgency')
              : t('settings.plan.hero.descSme')}
          </p>
        </CardContent>
      </Card>

      {/* Per-company caps inherited from the plan */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('settings.plan.limits.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <CapTile
              icon={Briefcase}
              label={t('settings.plan.limits.positions')}
              value={limits.positions}
            />
            <CapTile
              icon={Users}
              label={t('settings.plan.limits.candidates')}
              value={limits.candidates}
            />
            <CapTile
              icon={UserPlus}
              label={t('settings.plan.limits.seats')}
              value={limits.seats}
            />
            <CapTile
              icon={Zap}
              label={t('settings.plan.limits.aiCredits')}
              value={limits.aiCompany}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            {t('settings.plan.limits.hint')}
          </p>
        </CardContent>
      </Card>

      {/* Live usage — reuses the dashboard widgets so it stays in sync */}
      <CapUsageWidgets />

      {/* Plan features (read-only checklist).
          Source of truth: BE `MIN_PLAN_FOR_AI_FEATURES='freemium_trial'`
          (functions/src/lib/billing/billing-config.ts) — every AI action
          is functionally available on every paid plan AND on the trial
          (the trial just has a smaller AI credit allocation: 25 vs 300+).
          Only multi-company is plan-gated.

          Don't tie AI features to `!isTrial` — that was the original FE
          shipped 22/05/26 morning but the BE consents AI on trial. The
          marketing "Starter+ only" copy lives in the SubscriptionTab
          compare table (commercial gate, intentional). Here we describe
          what the customer CAN actually do with their current plan. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('settings.plan.features.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2.5">
            <FeatureRow
              included={true}
              label={t('settings.plan.features.aiScreening')}
              hint={isTrial ? t('settings.plan.features.trialCreditsHint') : undefined}
            />
            <FeatureRow
              included={true}
              label={t('settings.plan.features.callTranscription')}
              hint={isTrial ? t('settings.plan.features.trialCreditsHint') : undefined}
            />
            <FeatureRow
              included={true}
              label={t('settings.plan.features.interviewReports')}
              hint={isTrial ? t('settings.plan.features.trialCreditsHint') : undefined}
            />
            <FeatureRow
              included={true}
              label={t('settings.plan.features.aiAutofill')}
              hint={isTrial ? t('settings.plan.features.trialCreditsHint') : undefined}
            />
            <FeatureRow
              included={true}
              label={t('settings.plan.features.publicLandingPage')}
            />
            <FeatureRow
              included={true}
              label={t('settings.plan.features.emailNotifications')}
            />
            {AGENCY_SELF_SERVE_ENABLED && (
              <FeatureRow
                included={isAgencyPlan}
                label={t('settings.plan.features.multiCompany')}
              />
            )}
          </ul>
        </CardContent>
      </Card>

      {/* Footer note — owner-only CTA to profile billing */}
      {isOwner && (
        <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <p className="text-xs text-muted-foreground flex-1">
            {t('settings.plan.footer.note')}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-brand-primary hover:text-brand-primary hover:bg-brand-primary/10"
            onClick={() => navigate('/profile?tab=billing')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('settings.plan.cta.goToProfile')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface CapTileProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}

function CapTile({ icon: Icon, label, value }: CapTileProps) {
  return (
    <div className="rounded-lg border border-border/60 bg-background p-3 space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold flex items-center gap-1">
        <CapCell value={value} />
      </div>
    </div>
  );
}

function FeatureRow({ included, label, hint }: { included: boolean; label: string; hint?: string }) {
  return (
    <li className="flex items-start gap-2.5 text-sm">
      {included ? (
        <Check className="h-4 w-4 text-success shrink-0 mt-0.5" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <span className={included ? 'text-foreground' : 'text-muted-foreground/70 line-through'}>
          {label}
        </span>
        {hint && (
          <span className="block text-[11px] text-muted-foreground mt-0.5">{hint}</span>
        )}
      </div>
    </li>
  );
}

export default CompanyPlanTab;
